import { z } from 'zod';
import {
  equipmentComponentSchema,
  equipmentRaritySchema,
  equipmentTotalsSchema,
} from '../equipment';
import { jewelSocketProfileSchema } from '../inventory/jewel-profile';
import { cardSlotSchema } from '../inventory/loadout';
import { cannonRaritySchema, raritySchema } from '../rarity';
import { artifactDropSchema, artifactEffectsSchema, artifactsResponseSchema } from './artifacts';

import { activePetSchema } from './pets';
import { decimalStringSchema, digestSchema, MAX_DECIMAL_FIELD_DIGITS } from './primitives';

/**
 * One special drop a settlement confirmed, as the client is allowed to see it.
 *
 * Carries no seed, no digest, no denominator and no probability, for exactly
 * the reason the artifact wire does not: the draw is keyed on a secret, and
 * telling the browser anything about how it was made is the first step toward
 * predicting the next one. Only the FACT crosses, plus what the player needs to
 * read on the toast.
 *
 * One schema for both species because they come from ONE roll and are mutually
 * exclusive. The component fields are nullable and a stabiliser leaves them
 * null - it is a stack credit and genuinely has no instance, rarity or Power.
 */
export const equipmentDropSchema = z.object({
  /** Server-derived, stable, unique per account: the notification key. */
  dropId: z.string().min(1),
  kind: z.enum(['component', 'stabilizer']),
  definitionId: z.string().min(1),
  name: z.string().min(1),
  /** Component only. */
  instanceId: z.uuid().nullable(),
  component: equipmentComponentSchema.nullable(),
  rarity: equipmentRaritySchema.nullable(),
  /** Frozen at the moment of the grant, from the server's own calculation. */
  power: z.number().int().positive().nullable(),
  /** True when a boss produced it. */
  fromBoss: z.boolean(),
});
export type EquipmentDropWire = z.infer<typeof equipmentDropSchema>;

/**
 * **Uma carta que uma janela liquidada confirmou** (gate V28H.E).
 *
 * O mesmo silêncio das outras duas: nem semente, nem digest, nem denominador,
 * nem probabilidade. Só o FACTO, mais o que o jogador precisa de ler.
 *
 * Um esquema próprio e não `artifactDropSchema` reaproveitado: uma carta não
 * tem `assetKey` - a arte é de um gate futuro - e o `null` honesto ali obrigaria
 * o artefacto a admitir um campo anulável que ele nunca tem. E o cliente tem de
 * conseguir separar as três loterias sem adivinhar pelo id.
 */
export const cardDropSchema = z.object({
  /** Derivado do servidor, estável e único por conta: a chave da notificação. */
  dropId: z.string().min(1),
  definitionId: z.string().min(1),
  name: z.string().min(1),
  rarity: raritySchema,
  instanceId: z.uuid(),
  /** Sempre 1. Os níveis vêm de fundir duas cópias, nunca da sorte. */
  level: z.literal(1),
  /** Qual morte da sua espécie, dentro da sessão: a origem da queda. */
  killOrdinal: z.number().int().min(1),
  /** Verdadeiro quando um boss a produziu - seis vezes mais provável. */
  fromBoss: z.boolean(),
});
export type CardDropWire = z.infer<typeof cardDropSchema>;

/**
 * Hunt and progression wire contracts.
 *
 * Three rules shape every schema here:
 *
 * 1. **Requests are `.strict()`.** An unknown key is a rejected request, not
 *    an ignored one. A client that tries to send `clientGold`, `claimedXp` or
 *    a timestamp gets a 400 - the field cannot be "forgotten" by a handler.
 * 2. **The client never states an outcome.** It may say which session it is
 *    on, which sequence comes next, how much of the window its own simulation
 *    covered, and a digest of its own state. XP, Gold, levels, damage and
 *    elapsed time are all computed server-side.
 * 3. **Unbounded values cross as decimal strings.** KENOCYBER has no level
 *    cap: level, XP and points are `bigint`, and a JSON number would stop
 *    being exact long before the curve does.
 */

/** Re-exported so every existing importer of these keeps working unchanged. */
export { decimalStringSchema, digestSchema, MAX_DECIMAL_FIELD_DIGITS };

export const HUNT_BOOTSTRAP_ROUTE = '/api/hunt/bootstrap' as const;
export const HUNT_CHECKPOINT_ROUTE = '/api/hunt/checkpoint' as const;
export const HUNT_RESTART_ROUTE = '/api/hunt/restart' as const;
/** Read-only: the twenty planned maps and where the ship is now. */
export const MAP_LIST_ROUTE = '/api/maps' as const;
/** The ONE way a player changes map. There is no other. */
export const HUNT_TRAVEL_ROUTE = '/api/hunt/travel' as const;
export const PROGRESSION_ALLOCATE_ROUTE = '/api/progression/allocate' as const;
/**
 * **A ordem de DETONAR as marcas de sangramento, na caça** (gate V28G.N).
 *
 * ## Porque uma rota, e não um campo do checkpoint
 *
 * Porque o `CLAUDE.md` fecha o checkpoint por escrito: ele "may carry only
 * `sessionId`, `sequence`, `clientDeltaMs` and an idempotency key, plus an
 * optional state digest". Uma ação de combate não é nenhum desses, e enfiá-la lá
 * abriria o pedido que a regra existe para manter fechado.
 *
 * ## E porque não é uma decisão só do cliente
 *
 * Porque a caça não tem canal de input por tique: o servidor re-simula uma
 * janela a partir da instantânea e do TEMPO, e nada mais. Um cliente que
 * detonasse localmente produziria uma instantânea que a re-simulação do servidor
 * nunca alcança, e o digest seguinte seria uma correção - com as marcas de volta
 * e o dano por acontecer.
 *
 * O caminho autoritativo que já existe para uma ação manual do jogador a meio de
 * uma corrida é o da alocação de pontos: **liquidar, aplicar e reconfigurar a
 * MESMA sessão no lugar**, numa transação. Esta rota é esse caminho, com a mesma
 * forma, e não um protocolo paralelo.
 */
export const HUNT_BLEED_DETONATE_ROUTE = '/api/hunt/bleed/detonate' as const;
/** A ORDEM do jogador para ligar uma carta (gate V28H.I). */
export const HUNT_CARD_ACTIVATE_ROUTE = '/api/hunt/cards/activate' as const;
/**
 * **A ORDEM do jogador para SOLTAR uma carga** (gate V28H.N).
 *
 * ## Porque uma segunda rota, e não um campo da primeira
 *
 * Porque são dois actos do jogador em dois instantes diferentes, e cada acto
 * autoritativo desta API tem a sua ordem com a sua identidade. Enfiar "e agora
 * solta" no pedido de ligar obrigaria o cliente a saber, no instante em que
 * carrega na tecla, quanto tempo a vai segurar - que é precisamente a decisão
 * que a carta existe para lhe dar.
 *
 * Mesma forma, mesmo corpo, mesmo caminho: liquidar, aplicar e devolver a MESMA
 * sessão reconfigurada. Só o verbo muda.
 */
export const HUNT_CARD_RELEASE_ROUTE = '/api/hunt/cards/release' as const;
export const PLAYER_STATE_ROUTE = '/api/player/state' as const;

/**
 * The checkpoint cadence, and the MINIMUM window the server will credit.
 *
 * A checkpoint sent earlier is refused outright: nothing is written, no
 * sequence is consumed and no ledger row appears. That is what stops a client
 * spamming thousands of tiny windows to inflate its offline farm rate.
 */
export const CHECKPOINT_INTERVAL_MS = 30_000;

/**
 * How far short of the interval a client's own simulated time may fall and
 * still count as a full window: exactly one fixed tick (16 ms). Frames land on
 * tick boundaries, so 29 984 ms is a complete 30 s window, not a short one.
 */
export const CHECKPOINT_CLIENT_TOLERANCE_MS = 16;

/**
 * A session with no checkpoint for this long can be taken over by a new one,
 * and it is also the ceiling on the delta a single checkpoint may report, so a
 * laptop lid closed for an hour becomes offline farm rather than online farm.
 */
export const HUNT_SESSION_LEASE_MS = 90_000;

/* -------------------------------------------------------------------------- */
/*  Shared response shapes                                                    */
/* -------------------------------------------------------------------------- */

export const progressionSchema = z.object({
  /** No level cap: a decimal string, never a JSON number. */
  level: decimalStringSchema,
  /** XP toward the next level. */
  xpIntoLevel: decimalStringSchema,
  /** Every XP point ever earned; the level derives from it. */
  lifetimeXp: decimalStringSchema,
  xpForNextLevel: decimalStringSchema,
  freePoints: decimalStringSchema,
  damagePoints: decimalStringSchema,
  healthPoints: decimalStringSchema,
  attackSpeedPoints: decimalStringSchema,
  /** Small and bounded by design, so a plain number is honest here. */
  currentMap: z.number().int().min(1),
  progressionVersion: z.number().int().min(1),
  /** Confirmed Gold balance, summed from the ledger. */
  gold: decimalStringSchema,
});
export type ProgressionWire = z.infer<typeof progressionSchema>;

export const huntSessionStateSchema = z.object({
  sessionId: z.uuid(),
  /** Public simulation seed as a decimal uint64 string. */
  seed: z.string().regex(/^\d{1,20}$/),
  mapIndex: z.number().int().min(1),
  configVersion: z.number().int().min(1),
  /**
   * Simulated time the server has accepted, ms. A session that runs for weeks
   * stays inside `Number.MAX_SAFE_INTEGER` by many orders of magnitude.
   */
  acceptedTickMs: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  /** Sequence of the last accepted checkpoint; the next must be this + 1. */
  lastSequence: z.number().int().min(0),
  /** False for debug sessions: they never earn and are refused at checkpoint. */
  economic: z.boolean(),
  /**
   * The equipment totals this session is FROZEN to, summed.
   *
   * On the session rather than beside it, because that is what they are: a
   * copy taken when the run was configured, exactly like the loadout and the
   * artifact totals, and not the account's current components.
   *
   * They travel because the browser simulates and the server re-simulates, and
   * the two have to agree. This was missing: the client had no way to know
   * what the equipped components were worth, so it fought with none of them -
   * the ship in the arena dealt base damage while the server credited the real
   * figure, and every window ended in a correction.
   */
  equipmentTotals: equipmentTotalsSchema,
  checkpointIntervalMs: z.number().int().min(1000),
});
export type HuntSessionStateWire = z.infer<typeof huntSessionStateSchema>;

/**
 * The equipped loadout, derived from real owned instances (ADR 0005).
 *
 * Carries the instance ids so the client can show WHICH item is equipped, and
 * the numbers it needs to render - never audit rows, operation ids, hashes or
 * anything about another account. The client reads these; it can never send
 * them: no request in this API accepts a loadout, and the server recomputes
 * every value from the catalog anyway.
 */
export const loadoutSchema = z.object({
  ship: z.object({
    instanceId: z.uuid(),
    definitionId: z.string().min(1),
    /** Decides which ship PNG is drawn. */
    shipIndex: z.number().int().min(1),
    rarity: raritySchema,
    level: z.number().int().min(1),
    /** Damage bonus in basis points, computed server-side. */
    damageBonusBps: z.number().int().min(0),
  }),
  cannon: z.object({
    instanceId: z.uuid(),
    definitionId: z.string().min(1),
    rarity: cannonRaritySchema,
    level: z.number().int().min(1),
    /** Formation size for the level. */
    cannonCount: z.number().int().min(1),
    /** Projectiles each cannon emits; decided by the rarity. */
    projectilesPerCannon: z.number().int().min(1),
  }),
  /**
   * The worn skin, or null (gate V23.1B).
   *
   * ## Additive in both directions, deliberately
   *
   * This contract has no version field and never had one; its change policy is
   * additive, and `.default(null)` is what makes this change honour that in
   * BOTH directions. An old server's answer - which has no `skin` key - parses
   * here into an explicit null rather than into `undefined`, so every reader
   * downstream sees one shape. An old client ignores the key it does not know.
   * A bump was not invented, and an incompatibility is not being hidden: there
   * is none to hide.
   *
   * ## Presentation identity only
   *
   * `definitionId` is a content identity resolved against the client's own
   * validated manifest. `instanceId` is opaque and says which copy is worn.
   * There is no `socketCapacity` here, and there is no field for a jewel, an
   * attribute, a seed, a rolled point, a banner or an origin - a renderer that
   * received one would be a renderer that could one day read it.
   */
  skin: z
    .object({
      instanceId: z.uuid(),
      definitionId: z.string().min(1),
    })
    .nullable()
    .default(null),
  /**
   * As joias encaixadas na skin equipada, agregadas (gate V28G.C).
   *
   * ## Porque o cliente precisa disto
   *
   * Para DESENHAR. A Chama da Destruição tem uma assinatura visual própria, e a
   * cena tem de saber se este casco a carrega - uma pergunta que só o perfil
   * responde. Inferir do dano seria errado: o Coração Primordial também o sobe,
   * e a nave acenderia fogo por ter corações.
   *
   * ## Aditivo, e sem versão
   *
   * Este contrato não carrega versão e a sua política é aditiva. `.default`
   * significa que uma resposta de um servidor anterior parseia como zero joias -
   * o que essas sessões realmente tinham - e um cliente anterior ignora a chave.
   *
   * ## O que continua a NÃO atravessar
   *
   * Nenhum UUID de joia, de skin ou de socket, e nenhuma capacidade. O perfil é
   * agregado por construção, e é a mesma fronteira que o gate V28G.A desenhou
   * para o bilhete do PvP.
   */
  jewels: jewelSocketProfileSchema.default(() => ({ entries: [] })),
});
export type LoadoutWire = z.infer<typeof loadoutSchema>;

export const offlineRewardSchema = z.object({
  /** Offline time actually credited, after the 6 h cap. */
  creditedMs: z.number().int().min(0),
  xpGained: decimalStringSchema,
  goldGained: decimalStringSchema,
  /** Always false. Stated on the wire so the UI cannot imply otherwise. */
  itemsGranted: z.literal(false),
  /** Levels gained while away, if any. */
  levelsGained: decimalStringSchema,
});
export type OfflineRewardWire = z.infer<typeof offlineRewardSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/hunt/bootstrap                                                  */
/* -------------------------------------------------------------------------- */

export const huntBootstrapRequestSchema = z
  .object({
    /**
     * Declares a DEBUG session (dev builds only). Declaring it can only cost
     * the caller: such a session is marked non-economic and is refused at
     * checkpoint, so debug tooling can never mint progress.
     */
    debug: z.boolean().optional(),
  })
  .strict();
export type HuntBootstrapRequest = z.infer<typeof huntBootstrapRequestSchema>;

/**
 * The boss encounter, as the wire carries it.
 *
 * A discriminated union so a SCHEDULED encounter has nowhere to put a tier.
 * The contract itself is the guarantee: there is no optional field somebody
 * could forget to strip, and a scheduled projection that carried a tier would
 * fail validation rather than reach a browser.
 */
export const publicBossEncounterSchema = z.discriminatedUnion('state', [
  z
    .object({
      state: z.literal('scheduled'),
      encounterId: z.string().min(1),
      /** Milliseconds of ACTIVE hunting still owed before the boss arrives. */
      remainingMs: z.number().int().min(0),
      mapIndex: z.number().int().min(1),
    })
    .strict(),
  z
    .object({
      state: z.literal('active'),
      encounterId: z.string().min(1),
      /** Only here. The server has already confirmed the boss is on the field. */
      tier: z.number().int().min(1).max(6),
      visualScaleBps: decimalStringSchema,
      mapIndex: z.number().int().min(1),
    })
    .strict(),
]);

export const huntBootstrapResponseSchema = z.object({
  /**
   * What this run is waiting for, or fighting.
   *
   * Present so the interface never has to read it out of the snapshot, and so
   * the countdown for the NEXT boss can travel in the very answer that
   * confirmed the last one died - which is what removes the interval in which
   * a player had nothing to look at.
   */
  bossEncounter: publicBossEncounterSchema.nullable(),
  session: huntSessionStateSchema,
  progression: progressionSchema,
  loadout: loadoutSchema,
  /**
   * The artifact passives FROZEN into this session, summed.
   *
   * The client simulates with exactly these, because the server re-simulates
   * with exactly these. It is the session's copy, not the account's current
   * artifacts: a copy obtained or merged later reconfigures the run through
   * its own message, and never rewrites what a played second was worth.
   */
  artifactTotals: artifactEffectsSchema,
  /**
   * The ACTIVE pet this session is bound to, or null when none is.
   *
   * Frozen with the run like the loadout and the artifact totals: the client
   * draws and simulates the pet the server will re-simulate with, and decides
   * none of it. Null is the normal state - a hunt without a pet is complete.
   */
  activePet: activePetSchema.nullable(),
  /**
   * The authoritative deterministic state the browser must continue from.
   *
   * Typed as unknown on the wire on purpose: its real contract is the
   * versioned validator in `@kenocyber/game-domain` (`parseHuntSnapshot`),
   * which the client runs before restoring.
   */
  snapshot: z.unknown(),
  /** Digest of that snapshot, so the client can confirm what it restored. */
  snapshotDigest: digestSchema,
  /** Present only when time away actually produced a reward. */
  offlineReward: offlineRewardSchema.nullable(),
  /** True when an existing session was resumed rather than started. */
  resumed: z.boolean(),
  /**
   * Present only while the hunt is SUSPENDED for another modality (gate
   * V28I.BZ, ADR 0128).
   *
   * The account is on an open-world expedition. The session above is the run
   * the entry closed, handed over only so the one canvas both modes share can
   * mount; it is never checkpointed. Nothing was settled, granted or opened to
   * produce this answer. The client stops its cadence and restores the
   * expedition, and the hunt comes back through ONE bootstrap once it ends.
   */
  suspendedFor: z.literal('open_world').optional(),
});
export type HuntBootstrapResponse = z.infer<typeof huntBootstrapResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/hunt/checkpoint                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Everything a client is allowed to say about a checkpoint.
 *
 * Note what is absent and rejected by `.strict()`: earned XP, earned Gold,
 * kills, damage, and any timestamp. `clientDeltaMs` is a DELTA for this window
 * only, bounded by the lease - an absolute tick would eventually need a
 * ceiling, and a session that runs for days must not hit one.
 */
export const huntCheckpointRequestSchema = z
  .object({
    sessionId: z.uuid(),
    /** Must be exactly the server's `lastSequence + 1`. */
    sequence: z.number().int().min(1),
    /**
     * Simulated time the browser covered since the last accepted checkpoint.
     * An upper bound on what may be credited, never a grant.
     */
    clientDeltaMs: z.number().int().min(0).max(HUNT_SESSION_LEASE_MS),
    /**
     * Digest of the client's own deterministic state at that point. Used ONLY
     * to detect divergence: it can never authorise a reward.
     */
    clientDigest: digestSchema.optional(),
    /** Replay guard for THIS request. Never used as the ledger key. */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type HuntCheckpointRequest = z.infer<typeof huntCheckpointRequestSchema>;

/**
 * Sent only when the client's digest disagrees with the server's.
 *
 * The common case carries no snapshot at all: shipping one every 30 s to every
 * player would waste bandwidth to say "nothing changed".
 */
export const huntCorrectionSchema = z.object({
  authoritativeTickMs: z.number().int().min(0),
  authoritativeDigest: digestSchema,
  authoritativeSnapshot: z.unknown(),
});
export type HuntCorrectionWire = z.infer<typeof huntCorrectionSchema>;

export const huntCheckpointResponseSchema = z.object({
  session: huntSessionStateSchema,
  progression: progressionSchema,
  /** What THIS checkpoint credited, as computed by the server. */
  credited: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
    /** True when the window was long enough to fund offline farm. */
    completeWindow: z.boolean(),
  }),
  /** Null when the two simulations agree, which is the normal case. */
  correction: huntCorrectionSchema.nullable(),
  /**
   * The encounter the run is on AFTER this window.
   *
   * On the checkpoint that confirms a boss died, this is already its
   * SUCCESSOR: drawn, written and bound to the same simulation before the
   * answer was built. The countdown starts from the persisted delay, and the
   * client never invents a provisional one.
   */
  bossEncounter: publicBossEncounterSchema.nullable(),
  /**
   * Artifacts this window confirmed. Empty on almost every checkpoint.
   *
   * This is the ONLY way a drop becomes visible: the client announces nothing
   * until the server says so, and the longest that can take is one window.
   * There is no polling anywhere, and there is no request per kill.
   */
  drops: z.array(artifactDropSchema),
  /*
   * The special drops the same window confirmed: components and Quantum
   * Stabilisers. A SEPARATE array from the artifacts because it is a separate
   * roll - a kill can produce one of each, and merging them would lose which
   * table a given item came from.
   */
  equipmentDrops: z.array(equipmentDropSchema),
  /*
   * E as CARTAS que a mesma janela confirmou (gate V28H.E).
   *
   * Um terceiro array, pela mesma razão que o segundo existe: é um terceiro
   * sorteio. Uma morte pode produzir uma de cada, e juntá-los perderia de que
   * tabela cada item veio.
   *
   * Uma carta não muda nada na corrida - não tem espaço congelado, não tem
   * cooldown em simulação e não tem habilidade - por isso, ao contrário de um
   * artefacto, ela não traz passivas novas a seguir a si.
   */
  cardDrops: z.array(cardDropSchema),
  /**
   * The passives that apply from this boundary on - present only when the
   * drop changed them. The client reconfigures the SAME simulation with it;
   * the kills that produced the artifact are not recomputed with it.
   */
  artifacts: artifactsResponseSchema.nullable(),
  /** True when the request replayed an already-credited checkpoint. */
  replayed: z.boolean(),
});
export type HuntCheckpointResponse = z.infer<typeof huntCheckpointResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/hunt/restart                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One map, as the map screen sees it.
 *
 * Only maps the account may actually see are ever listed with `unlocked`
 * true; everything else is described identically whether it is reserved,
 * unbuilt or simply not earned. Nothing here reveals a boss tier.
 */
export const mapListEntrySchema = z
  .object({
    mapIndex: z.number().int().min(1),
    /** Derived from the index. Never stored, never accepted as input. */
    mapCode: z.string().min(1),
    name: z.string().min(1),
    unlocked: z.boolean(),
    /** True for exactly one entry: where the ship is now. */
    current: z.boolean(),
    /** True once this map's boss has been beaten in the current phase. */
    bossDefeated: z.boolean(),
    /** The map's mob scale in basis points, as a decimal string. */
    mobScaleBps: decimalStringSchema,
    /** False while the map has no alien, boss or background of its own. */
    contentReady: z.boolean(),
  })
  .strict();

export const mapListResponseSchema = z.object({
  currentMap: z.number().int().min(1),
  maps: z.array(mapListEntrySchema),
});

export type MapListEntry = z.infer<typeof mapListEntrySchema>;
export type MapListResponse = z.infer<typeof mapListResponseSchema>;

/**
 * A request to travel.
 *
 * `.strict()`, and it carries a destination and an idempotency key and
 * nothing else. No tier, no seed, no session state, no map code - the index is
 * the authority and the server re-derives everything else from it.
 */
export const huntTravelRequestSchema = z
  .object({
    mapIndex: z.number().int().min(1).max(50),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();

export type HuntTravelRequest = z.infer<typeof huntTravelRequestSchema>;

export const huntRestartRequestSchema = z
  .object({
    sessionId: z.uuid(),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type HuntRestartRequest = z.infer<typeof huntRestartRequestSchema>;

export const huntRestartResponseSchema = huntBootstrapResponseSchema;
export type HuntRestartResponse = z.infer<typeof huntRestartResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/progression/allocate                                            */
/* -------------------------------------------------------------------------- */

/**
 * Amounts are decimal strings and the only ceiling is the balance the player
 * owns: a player with 10^30 free points may spend them.
 */
export const progressionAllocateRequestSchema = z
  .object({
    damage: decimalStringSchema,
    health: decimalStringSchema,
    attackSpeed: decimalStringSchema,
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type ProgressionAllocateRequest = z.infer<typeof progressionAllocateRequestSchema>;

/**
 * The answer carries the whole authoritative state to keep playing on.
 *
 * Spending a point changes the attributes, which changes the loadout, which
 * retires the running session - so the replacement is created by the same
 * operation and handed back here. The client adopts it directly: asking it to
 * bootstrap again would leave it session-less in between, with any checkpoint
 * still in flight coming back as a conflict.
 */
export const progressionAllocateResponseSchema = z.object({
  progression: progressionSchema,
  session: huntSessionStateSchema,
  /** Unchanged by an allocation; stated so the client need not assume it. */
  loadout: loadoutSchema,
  /** Unchanged too - spending a point does not touch the vault. */
  artifactTotals: artifactEffectsSchema,
  /** Unchanged as well; stated so the client need not assume it. */
  activePet: activePetSchema.nullable(),
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  /** What the tail earned before the points were spent. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
  /** True when the same key was already applied; nothing was spent again. */
  replayed: z.boolean(),
});
export type ProgressionAllocateResponse = z.infer<typeof progressionAllocateResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/hunt/bleed/detonate                                             */
/* -------------------------------------------------------------------------- */

/**
 * **O que o cliente pode dizer para detonar: qual sessão, e mais nada.**
 *
 * `.strict()` como todo o pedido autoritativo desta API. Não há alvo, não há
 * contagem, não há magnitude e não há lista de marcas - a autoridade decide tudo
 * isso a partir da instantânea que ela própria guardou, e um campo aqui seria a
 * primeira coisa que alguém tentava usar.
 *
 * ## Porque a sessão viaja
 *
 * Pela mesma razão que ela viaja num checkpoint: uma resposta que pertença a uma
 * sessão substituída tem de ser recusada, não aplicada. Uma ordem enviada
 * enquanto uma reconfiguração estava em voo não pode detonar marcas de uma
 * corrida que já não é aquela.
 *
 * ## Porque NÃO há chave de idempotência
 *
 * Porque a operação já é idempotente no facto, e por construção: uma detonação
 * consome todas as marcas, portanto uma repetição encontra zero e é um `no-op`
 * seguro que não escreve dano nenhum. Uma chave protegeria contra uma segunda
 * detonação que não pode existir - e daria a impressão de que uma existe.
 */
export const huntBleedDetonateRequestSchema = z
  .object({
    sessionId: z.string().uuid(),
  })
  .strict();
export type HuntBleedDetonateRequest = z.infer<typeof huntBleedDetonateRequestSchema>;

/**
 * Um alvo que rebentou, e o que rebentou nele.
 *
 * Apresentação, e derivada de um facto que a autoridade acabou de produzir. A
 * cena precisa dos três para desenhar o que o contrato aprova: onde (o alvo),
 * com que intensidade (a contagem), e que número imprimir (o dano aplicado).
 *
 * `applied` viaja porque na caça o evento `hit` da detonação **não** chega ao
 * cliente: ela acontece dentro desta transação, e o servidor drena a fila de
 * apresentação depois de re-simular. Sem este campo o jogador via a barra descer
 * sem um número.
 */
export const huntBleedDetonationTargetSchema = z
  .object({
    targetId: z.number().int(),
    marks: z.number().int().min(1),
    /** O dano REALMENTE aplicado, depois do clamp de overkill. */
    applied: z.number().min(0),
  })
  .strict();

/**
 * A resposta carrega o estado autoritativo inteiro para continuar a jogar.
 *
 * A mesma forma da alocação de pontos, e pela mesma razão: a cauda por liquidar
 * foi creditada, a MESMA sessão continua com a instantânea nova, e a cena
 * reconfigura-se no lugar - a vaga, a contagem do boss e os tiros no ar são da
 * corrida em que o jogador está.
 */
export const huntBleedDetonateResponseSchema = z.object({
  progression: progressionSchema,
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  /** O que rebentou, por alvo. Vazio quando não havia marcas - um `no-op`. */
  detonated: z.array(huntBleedDetonationTargetSchema),
  /** O que a cauda rendeu antes de a ordem ser executada. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
});
export type HuntBleedDetonateResponse = z.infer<typeof huntBleedDetonateResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  POST /api/hunt/cards/activate                                             */
/* -------------------------------------------------------------------------- */

/**
 * **A ordem do jogador: qual sessão, qual espaço, e qual ordem.**
 *
 * `.strict()` como todo o pedido autoritativo desta API. Três campos, e nenhum
 * deles é um efeito:
 *
 * - **`sessionId`** pela mesma razão que ele viaja num checkpoint e numa
 *   detonação: uma resposta que pertença a uma sessão substituída tem de ser
 *   recusada, nunca aplicada. Uma ordem enviada enquanto uma reconfiguração
 *   estava em voo não pode ligar uma carta de uma corrida que já não é aquela.
 * - **`slot`** é a identidade do espaço, e o servidor confirma que ele pertence
 *   ao loadout CONGELADO da sessão. O cliente não diz que carta é: dizê-lo seria
 *   deixá-lo escolher.
 * - **`commandId`** é a identidade da ORDEM, e existe para a repetição ser
 *   segura. Sem ela, um pedido cuja resposta se perdeu voltaria a ser enviado,
 *   encontraria a carta já activa e receberia uma recusa por uma ordem que na
 *   verdade foi aceite - o jogador veria "indisponível" com os canhões montados.
 *
 * O que **não** viaja: a duração, o arrefecimento, o instante, o efeito, o
 * tique. Todos são decididos pela autoridade a partir da instantânea que ela
 * própria guarda.
 */
export const huntCardActivateRequestSchema = z
  .object({
    sessionId: z.string().uuid(),
    slot: cardSlotSchema,
    commandId: z.string().uuid(),
  })
  .strict();
export type HuntCardActivateRequest = z.infer<typeof huntCardActivateRequestSchema>;

/**
 * A recusa NOMEADA de uma ordem.
 *
 * Um código e não uma frase: a frase é da interface e muda com o idioma, o
 * código é o contrato e é o que um teste pode fixar. Cada um corresponde a uma
 * das confirmações que a autoridade faz, e um deles - `slot_empty` - existe
 * para o caso em que o cliente e o servidor discordam sobre o que está equipado,
 * que é exatamente o caso em que o cliente não pode ter razão.
 */
export const HUNT_CARD_ACTIVATE_REFUSALS = [
  'slot_empty',
  'wrong_mode',
  'not_implemented',
  'ship_dead',
  'not_ready',
] as const;
export type HuntCardActivateRefusal = (typeof HUNT_CARD_ACTIVATE_REFUSALS)[number];

/**
 * As recusas de uma ordem de SOLTAR (gate V28H.N).
 *
 * As mesmas quatro perguntas ao loadout congelado, e depois uma diferente:
 * `not_ready` não faz sentido aqui - soltar não espera arrefecimento nenhum -
 * e `no_charge` ocupa o lugar dela. É a recusa de uma soltura que não encontra
 * carga: uma ordem repetida, uma carta que não carrega, ou uma carga que já se
 * completou sozinha entre o dedo do jogador e a chegada do pedido.
 */
export const HUNT_CARD_RELEASE_REFUSALS = [
  'slot_empty',
  'wrong_mode',
  'not_implemented',
  'ship_dead',
  'no_charge',
] as const;
export type HuntCardReleaseRefusal = (typeof HUNT_CARD_RELEASE_REFUSALS)[number];

/**
 * A resposta carrega o estado autoritativo inteiro para continuar a jogar.
 *
 * A mesma forma da detonação e da alocação de pontos, e pela mesma razão: a
 * cauda por liquidar foi creditada, a MESMA sessão continua com a instantânea
 * nova, e a cena reconfigura-se no lugar.
 *
 * `accepted` é falso numa RECUSA, e uma recusa continua a ser uma resposta 200
 * com estado: o pedido estava bem formado e a autoridade respondeu-lhe. Isso é
 * o que permite ao cliente corrigir a barra - se ele achava que a carta estava
 * disponível e ela não estava, a instantânea que vem aqui diz a verdade.
 */
export const huntCardActivateResponseSchema = z.object({
  accepted: z.boolean(),
  /** Nomeada quando `accepted` é falso, e `null` quando é verdadeiro. */
  refusal: z.enum(HUNT_CARD_ACTIVATE_REFUSALS).nullable(),
  /** Verdadeiro quando esta MESMA ordem já tinha sido aplicada. */
  replayed: z.boolean(),
  progression: progressionSchema,
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  /** O que a cauda rendeu antes de a ordem ser executada. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
});
export type HuntCardActivateResponse = z.infer<typeof huntCardActivateResponseSchema>;

/**
 * O pedido de SOLTAR: exactamente o mesmo do de ligar.
 *
 * Reexportado em vez de redeclarado, porque ele é o mesmo objecto e uma segunda
 * declaração seria uma segunda oportunidade de divergir. A sessão, o espaço e a
 * identidade da ordem - e nada que seja um efeito.
 */
export const huntCardReleaseRequestSchema = huntCardActivateRequestSchema;
export type HuntCardReleaseRequest = z.infer<typeof huntCardReleaseRequestSchema>;

/** A resposta de SOLTAR: a mesma, com as suas recusas. */
export const huntCardReleaseResponseSchema = huntCardActivateResponseSchema.extend({
  refusal: z.enum(HUNT_CARD_RELEASE_REFUSALS).nullable(),
});
export type HuntCardReleaseResponse = z.infer<typeof huntCardReleaseResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  GET /api/player/state                                                     */
/* -------------------------------------------------------------------------- */

export const playerStateResponseSchema = z.object({
  progression: progressionSchema,
  /** Null before the account's first bootstrap has equipped its starter kit. */
  loadout: loadoutSchema.nullable(),
  /** The active session, when one exists. */
  session: huntSessionStateSchema.nullable(),
  /** The active pet, or null. Having none is normal and permanent. */
  activePet: activePetSchema.nullable(),
});
export type PlayerStateResponse = z.infer<typeof playerStateResponseSchema>;
